// utils/auth.js
// ------------------------------------------------------------------
// Central helpers for storing / retrieving JWTs, refreshing tokens,
// and determining login state. Keeps naming explicit & DRY.
// ------------------------------------------------------------------

const LS_KEY = 'lokal_advertiser_jwt';            // { access, refresh }
const PROFILE_KEY = 'lokal_advertiser_profile';   // advertiser /me data
const LEGACY_KEY     = 'loginDetails'; 

const canUseLS = () => typeof window !== 'undefined';

/* ---------- defaults ---------- */
export const LOGIN_DEFAULT_DETAILS = {
  id            : null,     // ← “news_id” goes here once we fetch profile
  isLogIn       : false,
  mobileNumber  : '',
  name          : 'Name',
  whatsAppOptIn : false
};

/* ---------- token helpers ---------- */
export const saveTokens = ({ access, refresh }) => {
  if (!canUseLS() || !access) return;
  localStorage.setItem(
    LS_KEY,
    JSON.stringify({ access, refresh: refresh || '' })
  );
};

export const getTokens = () => {
  if (!canUseLS()) return {};
  try {
    return JSON.parse(localStorage.getItem(LS_KEY)) || {};
  } catch {
    return {};
  }
};

export const clearTokens = () => canUseLS() && localStorage.removeItem(LS_KEY);

/* ---------- advertiser profile cache ---------- */
export const saveAdvertiserProfile = (p) =>
  canUseLS() && localStorage.setItem(PROFILE_KEY, JSON.stringify(p || {}));

export const getAdvertiserProfile = () => {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_KEY)) || {};
  } catch {
    return {};
  }
};

export const clearAdvertiserProfile = () =>
  canUseLS() && localStorage.removeItem(PROFILE_KEY);

/* ---------- legacy loginDetails helpers ---------- */
const mergeLegacy = (patch = {}) => {
  const current  = getLegacyLoginDetails();
  const merged   = { ...LOGIN_DEFAULT_DETAILS, ...current, ...patch };
  localStorage.setItem(LEGACY_KEY, JSON.stringify(merged));
  return merged;
};

export const saveLegacyOnLogin = ({
  id           = null,
  mobileNumber = '',
  whatsAppOptIn= false
}) =>
  mergeLegacy({
    id,
    isLogIn      : true,
    mobileNumber,
    whatsAppOptIn
  });

// export const patchLegacyWithProfile = ({ id, name }) =>
//   mergeLegacy({ id, name });

export const clearLegacyLoginDetails = () => canUseLS() && localStorage.removeItem(LEGACY_KEY);

export const getLegacyLoginDetails = () => {
  try   { return JSON.parse(localStorage.getItem(LEGACY_KEY)) || LOGIN_DEFAULT_DETAILS; }
  catch { return LOGIN_DEFAULT_DETAILS; }
};

/* ---------- derived state ---------- */
export const isLoggedIn = () => {
  const hasJwt = Boolean(getTokens().access);
  const legacy = getLegacyLoginDetails().isLogIn;
  return hasJwt && legacy;                 // both must be true
};

/* ---------- one-stop logout ---------- */

export const clearAllAuth = () => {
  clearTokens();
  clearAdvertiserProfile();
  clearLegacyLoginDetails();
};

let migratingPromise = null;       

export const ensureJwtFromLegacy = async (legacyToken) => {
  if (!legacyToken) return;

  if (getTokens().access) return;

  if (!migratingPromise) {
    const axios = (await import('./axios')).default;
    migratingPromise = axios.get('/user/jwt-tokens/advertiser/me',
      { headers: { Authorization: `${legacyToken}` } })
      .then(({ data }) => data?.access && saveTokens(data))
      .catch((e) => {                   
        console.error('[JWT-migrate] failed', e);
      })
      .finally(() => { migratingPromise = null; });
  }
  return migratingPromise;              
};
